import { isReservedTopLevelPath } from '#/lib/reserved-paths'

export type SearchVisitType = 'owner' | 'repository' | 'file'

export interface SearchVisit {
  type: SearchVisitType
  href: string
  label: string
  description: string
  visitedAt: number
}

const STORAGE_KEY = 'kilio:recent-search-visits'
const MAX_VISITS = 8

function isSearchVisit(value: unknown): value is SearchVisit {
  if (!value || typeof value !== 'object') return false
  const visit = value as Partial<SearchVisit>
  return (
    (visit.type === 'owner' || visit.type === 'repository' || visit.type === 'file') &&
    typeof visit.href === 'string' &&
    visit.href.startsWith('/') &&
    !visit.href.startsWith('//') &&
    typeof visit.label === 'string' &&
    typeof visit.description === 'string' &&
    typeof visit.visitedAt === 'number' &&
    Number.isFinite(visit.visitedAt)
  )
}

function decodePathPart(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function searchVisitFromPathname(pathname: string, now = Date.now()): SearchVisit | null {
  const rawParts = pathname.split('/').filter(Boolean)
  if (rawParts.length === 0) return null
  const parts = rawParts.map(decodePathPart)
  if (isReservedTopLevelPath(parts[0])) return null

  if (parts.length === 1) {
    return {
      type: 'owner',
      href: `/${rawParts[0]}`,
      label: parts[0],
      description: 'Owner',
      visitedAt: now,
    }
  }

  const repository = `${parts[0]}/${parts[1]}`
  if (parts[2] === 'blob' && parts[3] && parts[4]) {
    const fileParts = parts.slice(4)
    const fileName = fileParts.at(-1) ?? fileParts.join('/')
    const directory = fileParts.slice(0, -1).join('/')
    return {
      type: 'file',
      href: pathname,
      label: fileName,
      description: directory ? `${repository} · ${directory}` : repository,
      visitedAt: now,
    }
  }

  return {
    type: 'repository',
    href: `/${rawParts[0]}/${rawParts[1]}`,
    label: repository,
    description: 'Repository',
    visitedAt: now,
  }
}

export function readRecentSearchVisits(storage?: Storage): SearchVisit[] {
  if (!storage) return []
  try {
    const value: unknown = JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]')
    return Array.isArray(value)
      ? value.filter(isSearchVisit).sort((a, b) => b.visitedAt - a.visitedAt).slice(0, MAX_VISITS)
      : []
  } catch {
    return []
  }
}

export function recordRecentSearchVisit(visit: SearchVisit, storage?: Storage): SearchVisit[] {
  const next = [
    visit,
    ...readRecentSearchVisits(storage).filter((candidate) => candidate.href !== visit.href),
  ].slice(0, MAX_VISITS)
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Browsing still works when storage is disabled or full.
  }
  return next
}
